import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Stack,
  Typography,
  Button,
  Card,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  InputAdornment,
} from '@mui/material';
import { ChevronLeft, RefreshCw, Landmark, PlusCircle, History } from 'lucide-react';
import { toast } from 'react-toastify';
import Page from '../components/Page';
import {
  getServiceProviders,
  syncServiceProviderBalances,
  getServices,
  getServiceProviderBanks,
  syncServiceProviderBanks,
  getServiceProviderAccount,
  getAvailableProcessors,
  adjustServiceProviderBalance,
} from '../api/services';
import { hasPermission, isAdminOrOwner, PERMISSIONS } from '../utils/hasPermission';

const cleanHost = (url) => {
  if (!url) return '';
  return url.replace(/^https?:\/\//i, '').replace(/\/.*$/, '');
};

const getProviderModeInfo = (p) => {
  if (!p) {
    return {
      label: 'Live API Synced',
      shortLabel: 'Live API Synced',
      bgcolor: '#DCFCE7',
      color: '#166534',
    };
  }
  if (p.supportsBalanceCheck === false || p.balanceMode === 'manual_ledger') {
    return {
      label: 'Manual Virtual Ledger (Auto-decrements)',
      shortLabel: 'Manual Virtual Ledger',
      bgcolor: '#FEF3C7',
      color: '#92400E',
    };
  }
  if (p.balanceMode === 'unlimited') {
    return {
      label: 'Unlimited Balance (Bypass)',
      shortLabel: 'Unlimited Mode',
      bgcolor: '#EFF6FF',
      color: '#1D4ED8',
    };
  }
  return {
    label: 'Live API Synced',
    shortLabel: 'Live API Synced',
    bgcolor: '#DCFCE7',
    color: '#166534',
  };
};

const formatCategoryName = (cat) => {
  if (!cat) return '';
  const map = {
    data: 'Data',
    airtime: 'Airtime',
    cable: 'Cable',
    electricity: 'Electricity',
    result_check: 'Result Check',
    resultcheck: 'Result Check',
    internet: 'Internet',
  };
  const key = String(cat).toLowerCase().trim();
  return map[key] || (cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase());
};

const formatLastChecked = (dateStr) => {
  const d = dateStr ? new Date(dateStr) : new Date();
  if (Number.isNaN(d.getTime())) return '30/09/2026, 10:08:32';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  return `${day}/${month}/${year}, ${time}`;
};

export default function ServiceProviderDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { user, activeApp } = useSelector((state) => state.auth);
  const isPrivileged = isAdminOrOwner(user, activeApp);
  const canManageProviders =
    isPrivileged ||
    hasPermission(user, [
      PERMISSIONS.SERVICES_WRITE,
      PERMISSIONS.PROCESSORS_MANAGE,
    ]);

  const [provider, setProvider] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [processors, setProcessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingBanks, setSyncingBanks] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [liveBankAccounts, setLiveBankAccounts] = useState([]);
  const [accountDetails, setAccountDetails] = useState(null);

  // Balance Adjustment Modal State
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [adjustAction, setAdjustAction] = useState('add');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjusting, setAdjusting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!isPrivileged) return;
    setLoading(true);
    try {
      const [provRes, srvRes, procRes] = await Promise.all([
        getServiceProviders(),
        getServices(),
        getAvailableProcessors().catch(() => ({ ok: false })),
      ]);

      if (procRes && procRes.ok && procRes.data?.data?.processors) {
        setProcessors(procRes.data.data.processors);
      }

      if (provRes.ok && provRes.data?.data) {
        const found = provRes.data.data.find((p) => String(p.id) === String(id));
        if (found) {
          setProvider(found);

          // Fetch live funding bank accounts & user identity details if available
          try {
            const [banksRes, accRes] = await Promise.all([
              getServiceProviderBanks(found.id),
              getServiceProviderAccount(found.id),
            ]);
            if (banksRes.ok && banksRes.data?.data) {
              const bList = banksRes.data.data.bankAccounts || banksRes.data.data;
              if (Array.isArray(bList) && bList.length > 0) {
                setLiveBankAccounts(bList);
              } else {
                setLiveBankAccounts([]);
              }
            }
            if (accRes.ok && accRes.data?.data) {
              const aData = accRes.data.data.accountDetails || accRes.data.data;
              if (aData) {
                setAccountDetails(aData);
              }
            }
          } catch (e) {
            // non-fatal fallback
          }
        } else {
          toast.error(`Service Provider #${id} not found.`);
        }
      }
      if (srvRes.ok && srvRes.data?.data) {
        setAllServices(srvRes.data.data);
      }
    } catch (err) {
      toast.error('Error fetching provider details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSyncBalance = async () => {
    setSyncing(true);
    try {
      const res = await syncServiceProviderBalances();
      if (res.ok && res.data?.data) {
        const found = res.data.data.find((p) => String(p.id) === String(id));
        if (found) setProvider(found);
        toast.success(`Wallet balance synced for ${provider?.name}!`);
      } else {
        toast.error('Failed to sync provider balance.');
      }
    } catch (err) {
      toast.error('Error syncing provider balance.');
    } finally {
      setSyncing(false);
    }
  };

  const handleConfirmBalanceAdjustment = async () => {
    if (!provider || !adjustAmount) return;
    const num = Number(adjustAmount);
    if (Number.isNaN(num) || num <= 0) {
      toast.error('Please enter a valid positive amount.');
      return;
    }

    setAdjusting(true);
    try {
      const res = await adjustServiceProviderBalance(provider.id, {
        action: adjustAction,
        amount: num,
        notes: adjustNotes.trim() || undefined,
      });

      if (res.ok) {
        toast.success(
          res.data?.message ||
            `Successfully ${adjustAction === 'add' ? 'topped up' : 'updated'} balance to ₦${(
              res.data?.data?.adjustment?.newBalance || num
            ).toLocaleString('en-NG')}!`
        );
        setTopUpModalOpen(false);
        setAdjustAmount('');
        setAdjustNotes('');
        await fetchData();
      } else {
        toast.error(res.data?.message || 'Failed to adjust balance.');
      }
    } catch (err) {
      toast.error('Error updating provider balance.');
    } finally {
      setAdjusting(false);
    }
  };

  const handleSyncBanks = async () => {
    if (!provider) return;
    setSyncingBanks(true);
    try {
      const res = await syncServiceProviderBanks(provider.id);
      if (res.ok && res.data?.data) {
        const bList = res.data.data.bankAccounts || res.data.data;
        if (Array.isArray(bList) && bList.length > 0) {
          setLiveBankAccounts(bList);
          toast.success(`Found and synced ${bList.length} virtual bank account(s) for ${provider.name}!`);
        } else {
          setLiveBankAccounts([]);
          toast.info(`No virtual bank accounts were returned by ${provider.name}.`);
        }
      } else {
        toast.error('Failed to sync bank accounts.');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error syncing bank accounts from provider.');
    } finally {
      setSyncingBanks(false);
    }
  };

  const handleCopy = (text, label) => {
    if (text) {
      navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard!`);
    }
  };

  // Find products routing through this provider
  const assignedProducts = useMemo(() => {
    if (!provider) return [];
    return allServices.filter((s) => {
      if (!s.useV2Engine || !s.processorRoutes) return false;
      return s.processorRoutes.some((r) => r.processorSlug === provider.slug);
    });
  }, [allServices, provider]);

  // Match provider to processor from super admin catalog
  const matchedProcessor = useMemo(() => {
    if (!provider || !processors || processors.length === 0) return null;

    const slug = (provider.slug || '').toLowerCase().trim();
    const cleanName = (provider.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const provHost = cleanHost(provider.baseUrl);

    return (
      // 1. Exact slug match
      processors.find((p) => (p.name || '').toLowerCase().trim() === slug) ||
      // 2. Slug prefix or contains match
      processors.find((p) => {
        const pName = (p.name || '').toLowerCase().trim();
        return pName && (slug.includes(pName) || pName.includes(slug));
      }) ||
      // 3. Name match
      processors.find((p) => {
        const pClean = (p.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        return pClean && (pClean === cleanName || pClean.includes(cleanName) || cleanName.includes(pClean));
      }) ||
      // 4. Base URL host match
      processors.find((p) => provHost && p.baseUrl && (cleanHost(p.baseUrl) === provHost || provHost.includes(cleanHost(p.baseUrl)) || cleanHost(p.baseUrl).includes(provHost))) ||
      // 5. Driver type fallback match
      processors.find((p) => p.driverType === provider.driverType) ||
      null
    );
  }, [provider, processors]);

  // Determine supported services from the processor
  const supportedCategories = useMemo(() => {
    // 1. If provider has explicit supportedCategories configured
    if (provider?.supportedCategories && Array.isArray(provider.supportedCategories) && provider.supportedCategories.length > 0) {
      return Array.from(new Set(provider.supportedCategories.map(formatCategoryName).filter(Boolean)));
    }

    // 2. If matched processor has supportedCategories (from super admin processor catalog)
    if (matchedProcessor?.supportedCategories && Array.isArray(matchedProcessor.supportedCategories) && matchedProcessor.supportedCategories.length > 0) {
      return Array.from(new Set(matchedProcessor.supportedCategories.map(formatCategoryName).filter(Boolean)));
    }

    // 3. If any products are routed to this provider
    if (assignedProducts.length > 0) {
      const cats = Array.from(new Set(assignedProducts.map((p) => p.service || p.category).filter(Boolean)));
      if (cats.length > 0) {
        return Array.from(new Set(cats.map(formatCategoryName).filter(Boolean)));
      }
    }

    // 4. Driver type defaults
    if (provider?.driverType === 'smeplug' || provider?.driverType === 'ogdams' || provider?.driverType === 'boltnet') {
      return ['Airtime', 'Data'];
    }

    return ['Airtime', 'Data', 'Cable', 'Electricity', 'Result Check'];
  }, [provider, matchedProcessor, assignedProducts]);

  // Live funding bank accounts (No dummy data!)
  const fundingAccounts = useMemo(() => {
    const defaultGradients = [
      'linear-gradient(135deg, #5C2A58 0%, #3B1B38 100%)',
      'linear-gradient(135deg, #3259E8 0%, #1D3CB0 100%)',
      'linear-gradient(135deg, #1D7A52 0%, #115336 100%)',
    ];

    if (liveBankAccounts && Array.isArray(liveBankAccounts) && liveBankAccounts.length > 0) {
      return liveBankAccounts.map((acc, index) => ({
        bankName: acc.bankName || acc.bank_name || acc.bank || 'Virtual Bank',
        accountName: acc.accountName || acc.account_name || accountDetails?.name || provider?.name || 'Funding Account',
        accountNumber: acc.accountNumber || acc.account_number || acc.accountNo || acc.number || '',
        gradient: defaultGradients[index % defaultGradients.length],
      }));
    }
    if (provider?.fundingAccounts && Array.isArray(provider.fundingAccounts) && provider.fundingAccounts.length > 0) {
      return provider.fundingAccounts;
    }
    return [];
  }, [liveBankAccounts, provider, accountDetails]);

  if (!isPrivileged) {
    return (
      <Page title="Service Provider | Access Denied">
        <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
          <Card variant="outlined" sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h5" color="error" fontWeight={800} sx={{ mb: 1.5 }}>
              Access Denied
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Service provider balances, API credentials, and vendor processor settings are restricted to workspace owners and administrators only.
            </Typography>
            <Button
              onClick={() => navigate('/dashboard/services')}
              variant="contained"
              sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
            >
              Go to Services
            </Button>
          </Card>
        </Container>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page title="Service Provider Details | Dashboard">
        <Container maxWidth="xl" sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={36} />
        </Container>
      </Page>
    );
  }

  if (!provider) {
    return (
      <Page title="Service Provider Details | Dashboard">
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Button
            onClick={() => navigate('/dashboard/service-providers')}
            startIcon={<ChevronLeft size={18} />}
            sx={{ color: '#0066FE', textTransform: 'none', fontWeight: 600, mb: 3 }}
          >
            Providers
          </Button>
          <Card sx={{ p: 5, textAlign: 'center', borderRadius: '16px' }}>
            <Typography variant="h6" color="text.secondary">
              Provider #{id} was not found.
            </Typography>
          </Card>
        </Container>
      </Page>
    );
  }

  const balanceNum = Number(provider.balance || 0);
  const thresholdNum = Number(provider.lowBalanceThreshold || 1000);
  const totalSpentNum = Number(provider.totalSpent || 0);
  const maxRange = Math.max(10000, thresholdNum * 5, balanceNum * 1.25);
  const progressPercent = Math.min(100, Math.max(2, (balanceNum / maxRange) * 100));
  const thresholdPercent = Math.min(98, Math.max(2, (thresholdNum / maxRange) * 100));
  const initial = (provider.name || 'P').trim().charAt(0).toUpperCase();
  const hostLabel = cleanHost(provider.baseUrl) || `${provider.slug}.com.ng`;

  return (
    <Page title={`${provider.name} | Dashboard`}>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        {/* Navigation & Actions Top Bar */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography
            component="button"
            onClick={() => navigate('/dashboard/service-providers')}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              background: 'none',
              border: 'none',
              p: 0,
              color: '#0066FE',
              fontWeight: 500,
              fontSize: '0.925rem',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <ChevronLeft size={16} /> Providers
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              onClick={handleSyncBanks}
              disabled={syncingBanks}
              startIcon={syncingBanks ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
              sx={{
                borderRadius: '24px',
                bgcolor: '#EAECEF',
                color: '#1F2937',
                px: 2.25,
                py: 0.85,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#DFE2E6',
                  boxShadow: 'none',
                },
              }}
            >
              {syncingBanks ? 'Syncing banks...' : 'Sync banks'}
            </Button>
            <Button
              onClick={handleSyncBalance}
              disabled={syncing}
              startIcon={syncing ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{
                borderRadius: '24px',
                bgcolor: '#EAECEF',
                color: '#1F2937',
                px: 2.5,
                py: 0.85,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#DFE2E6',
                  boxShadow: 'none',
                },
              }}
            >
              {syncing ? 'Syncing...' : 'Sync balance'}
            </Button>
            {canManageProviders && (
              <Button
                onClick={() => setTopUpModalOpen(true)}
                startIcon={<PlusCircle size={15} />}
                sx={{
                  borderRadius: '24px',
                  bgcolor: '#EFF6FF',
                  color: '#0066FE',
                  border: '1px solid #BFDBFE',
                  px: 2.25,
                  py: 0.85,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#DBEAFE',
                    boxShadow: 'none',
                  },
                }}
              >
                Top Up Balance
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<History size={16} />}
              onClick={() => navigate(`/dashboard/audit-logs?search=${provider.slug || provider.name}`)}
              sx={{
                borderRadius: '24px',
                borderColor: '#E5E7EB',
                color: '#374151',
                px: 2.25,
                py: 0.85,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                '&:hover': {
                  borderColor: '#D1D5DB',
                  bgcolor: '#F9FAFB',
                },
              }}
            >
              Audit Trail
            </Button>
            <Button
              onClick={() => navigate(`/dashboard/service-providers/edit/${provider.id}`)}
              sx={{
                borderRadius: '24px',
                bgcolor: '#0066FE',
                color: '#FFFFFF',
                px: 2.75,
                py: 0.85,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#0052CC',
                  boxShadow: 'none',
                },
              }}
            >
              Edit
            </Button>
          </Stack>
        </Stack>

        {/* Provider Title & Avatar */}
        <Stack direction="row" alignItems="center" spacing={2} mb={3.5}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '12px',
              bgcolor: '#22252A',
              color: '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1.25rem',
              flexShrink: 0,
            }}
          >
            {initial}
          </Box>
          <div>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  fontSize: { xs: '1.5rem', sm: '1.85rem' },
                }}
              >
                {provider.name}
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  px: 1.25,
                  py: 0.35,
                  borderRadius: '16px',
                  bgcolor: provider.isActive ? '#DCFCE7' : '#F3F4F6',
                  color: provider.isActive ? '#16A34A' : '#6B7280',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: provider.isActive ? '#16A34A' : '#6B7280',
                  }}
                />
                <span>{provider.isActive ? 'Active' : 'Inactive'}</span>
              </Box>
            </Stack>
            <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.875rem', mt: 0.25 }}>
              Provider #{provider.id} · {hostLabel}
              {accountDetails && (accountDetails.username || accountDetails.email || accountDetails.name) && (
                <span> · User: <strong>{accountDetails.username || accountDetails.email || accountDetails.name}</strong></span>
              )}
            </Typography>
          </div>
        </Stack>

        {/* Top 3 Metrics Cards (Left: Balance Bar, Right: Low Limit & Volume) */}
        <Grid container spacing={3} mb={4.5}>
          {/* Left: Wallet Balance with Progress Bar */}
          <Grid item xs={12} md={7}>
            <Card
              sx={{
                p: 3.5,
                borderRadius: '20px',
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                border: '1px solid #ECEEF1',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <div>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.875rem', fontWeight: 500, mb: 0.5 }}>
                      Wallet balance
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '2rem', sm: '2.5rem' },
                        fontWeight: 800,
                        color: balanceNum <= 0 ? '#DC2626' : '#111827',
                        letterSpacing: '-0.02em',
                        lineHeight: 1.1,
                      }}
                    >
                      ₦{balanceNum.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </div>
                  {canManageProviders && (
                    <Button
                      variant="contained"
                      onClick={() => setTopUpModalOpen(true)}
                      startIcon={<PlusCircle size={15} />}
                      sx={{
                        borderRadius: '20px',
                        bgcolor: '#0066FE',
                        color: '#FFFFFF',
                        px: 2,
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.825rem',
                        boxShadow: 'none',
                        '&:hover': {
                          bgcolor: '#0052CC',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Top Up / Add Funds
                    </Button>
                  )}
                </Stack>

                {/* Horizontal Progress Bar with Threshold Marker */}
                <Box sx={{ position: 'relative', width: '100%', mb: 1 }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 6,
                      borderRadius: 3,
                      bgcolor: '#E5E7EB',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: `${progressPercent}%`,
                        height: '100%',
                        bgcolor: balanceNum <= 0 ? '#DC2626' : '#16A34A',
                        borderRadius: 3,
                        transition: 'width 0.4s ease',
                      }}
                    />
                  </Box>

                  {/* Vertical Alert Marker */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${thresholdPercent}%`,
                      top: -3,
                      width: 2.5,
                      height: 12,
                      bgcolor: '#F59E0B',
                      borderRadius: '1px',
                    }}
                  />
                </Box>

                {/* Range Labels */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500 }}>
                    ₦0
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500 }}>
                    Alert below ₦{thresholdNum.toLocaleString()}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500 }}>
                    ₦{maxRange.toLocaleString()}
                  </Typography>
                </Box>
              </div>

              {/* Mode Badge & Last Checked */}
              <Stack direction="row" spacing={1} alignItems="center" mt={3} flexWrap="wrap">
                <Chip
                  size="small"
                  label={getProviderModeInfo(provider).label}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    bgcolor: getProviderModeInfo(provider).bgcolor,
                    color: getProviderModeInfo(provider).color,
                  }}
                />
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280' }}>
                  · Last updated {formatLastChecked(provider.lastBalanceCheckAt || provider.updatedAt)}
                </Typography>
              </Stack>
            </Card>
          </Grid>

          {/* Right: Low Balance Alert & Total Volume Processed */}
          <Grid item xs={12} md={5}>
            <Stack spacing={2.5} sx={{ height: '100%' }}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: '20px',
                  bgcolor: '#FFFFFF',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  border: '1px solid #ECEEF1',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: 500, mb: 0.5 }}>
                  Low balance alert
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1.75rem',
                    fontWeight: 800,
                    color: '#D97706',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  ₦{thresholdNum.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography sx={{ color: '#6B7280', fontSize: '0.825rem' }}>
                  We notify you below this amount.
                </Typography>
              </Card>

              <Card
                sx={{
                  p: 3,
                  borderRadius: '20px',
                  bgcolor: '#FFFFFF',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                  border: '1px solid #ECEEF1',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: 500, mb: 0.5 }}>
                  Total volume processed
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1.75rem',
                    fontWeight: 800,
                    color: '#111827',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  ₦{totalSpentNum.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography sx={{ color: '#6B7280', fontSize: '0.825rem' }}>
                  {totalSpentNum > 0 ? 'Cumulative spend across orders.' : 'No orders through this provider yet.'}
                </Typography>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        {/* Services Section */}
        <Box mb={4.5}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', mb: 2 }}>
            Services
          </Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {supportedCategories.map((cat) => (
              <Box
                key={cat}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  px: 2,
                  py: 0.75,
                  borderRadius: '24px',
                  bgcolor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  color: '#111827',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#16A34A' }} />
                <span>{cat}</span>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Funding accounts Section */}
        <Box mb={4.5}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1.5}
            flexWrap="wrap"
            gap={1.5}
          >
            <div>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827' }}>
                Funding accounts
              </Typography>
              <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.9rem', mt: 0.25 }}>
                Transfer to any of these accounts to top up this provider's wallet.
              </Typography>
            </div>

            <Button
              onClick={handleSyncBanks}
              disabled={syncingBanks}
              startIcon={syncingBanks ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
              sx={{
                borderRadius: '24px',
                bgcolor: '#EAECEF',
                color: '#1F2937',
                px: 2.25,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#DFE2E6',
                  boxShadow: 'none',
                },
              }}
            >
              {syncingBanks ? 'Syncing...' : 'Sync banks'}
            </Button>
          </Box>

          {fundingAccounts.length === 0 ? (
            <Card
              sx={{
                p: { xs: 4, sm: 5 },
                borderRadius: '20px',
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                border: '1px solid #ECEEF1',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  bgcolor: '#F3F4F6',
                  color: '#4B5563',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <Landmark size={24} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#111827', mb: 0.75 }}>
                No funding accounts available
              </Typography>
              <Typography
                sx={{
                  color: '#6B7280',
                  fontSize: '0.875rem',
                  maxWidth: 480,
                  lineHeight: 1.5,
                  mb: 2.5,
                }}
              >
                No virtual top-up bank accounts were returned by {provider.name}. Click &ldquo;Sync banks&rdquo; to fetch accounts, or top up your wallet directly via your vendor dashboard.
              </Typography>
              <Button
                onClick={handleSyncBanks}
                disabled={syncingBanks}
                startIcon={syncingBanks ? <CircularProgress size={14} color="inherit" /> : <RefreshCw size={14} />}
                sx={{
                  borderRadius: '24px',
                  bgcolor: '#0066FE',
                  color: '#FFFFFF',
                  px: 2.75,
                  py: 0.85,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#0052CC',
                    boxShadow: 'none',
                  },
                }}
              >
                {syncingBanks ? 'Syncing...' : 'Sync bank accounts'}
              </Button>
            </Card>
          ) : (
            <Grid container spacing={2.5}>
              {fundingAccounts.map((acc, idx) => (
                <Grid item xs={12} sm={4} key={idx}>
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: '20px',
                      background: acc.gradient,
                      color: '#FFFFFF',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minHeight: 180,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    }}
                  >
                    <div>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#FFFFFF', lineHeight: 1.2 }}>
                        {acc.bankName}
                      </Typography>
                      <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)', mt: 0.25 }}>
                        {acc.accountName}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: { xs: '1.45rem', sm: '1.65rem' },
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          mt: 2.5,
                          fontFamily: 'monospace',
                          color: '#FFFFFF',
                        }}
                      >
                        {acc.accountNumber}
                      </Typography>
                    </div>

                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                      <Typography sx={{ fontSize: '0.775rem', color: 'rgba(255,255,255,0.7)' }}>
                        Virtual account
                      </Typography>
                      <Button
                        onClick={() => handleCopy(acc.accountNumber, acc.bankName)}
                        size="small"
                        sx={{
                          borderRadius: '16px',
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: '#FFFFFF',
                          px: 1.75,
                          py: 0.35,
                          fontSize: '0.775rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.3)',
                          },
                        }}
                      >
                        Copy
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Credentials Section */}
        <Box mb={4.5}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', mb: 0.5 }}>
            Credentials
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.9rem', mb: 2 }}>
            Used to connect to the provider's API.
          </Typography>

          <Card
            sx={{
              borderRadius: '18px',
              bgcolor: '#FFFFFF',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              border: '1px solid #ECEEF1',
              overflow: 'hidden',
            }}
          >
            {/* Row 1: API key */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2.5}
              sx={{ borderBottom: '1px solid #F3F4F6' }}
            >
              <Typography sx={{ color: '#6B7280', fontSize: '0.9rem', fontWeight: 500 }}>
                API key
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#111827' }}>
                  {showApiKey ? provider.apiKey || 'No key set' : '••••••••••••••••••••'}
                </Typography>
                <Typography
                  component="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  sx={{
                    background: 'none',
                    border: 'none',
                    p: 0,
                    color: '#0066FE',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {showApiKey ? 'Hide' : 'Show'}
                </Typography>
                <Typography
                  component="button"
                  onClick={() => handleCopy(provider.apiKey, 'API Key')}
                  sx={{
                    background: 'none',
                    border: 'none',
                    p: 0,
                    color: '#0066FE',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Copy
                </Typography>
              </Stack>
            </Box>

            {/* Row 2: Slug */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2.5}
              sx={{ borderBottom: '1px solid #F3F4F6' }}
            >
              <Typography sx={{ color: '#6B7280', fontSize: '0.9rem', fontWeight: 500 }}>
                Slug
              </Typography>
              <Typography sx={{ color: '#111827', fontSize: '0.9rem', fontWeight: 500 }}>
                {provider.slug}
              </Typography>
            </Box>

            {/* Row 3: Base endpoint */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2.5}
              sx={{ borderBottom: '1px solid #F3F4F6' }}
            >
              <Typography sx={{ color: '#6B7280', fontSize: '0.9rem', fontWeight: 500 }}>
                Base endpoint
              </Typography>
              <Typography sx={{ color: '#111827', fontSize: '0.9rem', fontWeight: 500 }}>
                {provider.baseUrl || `https://api.${provider.slug}.com.ng`}
              </Typography>
            </Box>

            {/* Row 4: Status */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2.5}
            >
              <Typography sx={{ color: '#6B7280', fontSize: '0.9rem', fontWeight: 500 }}>
                Status
              </Typography>
              <Typography sx={{ color: provider.isActive ? '#16A34A' : '#6B7280', fontSize: '0.9rem', fontWeight: 600 }}>
                {provider.isActive ? 'Active' : 'Inactive'}
              </Typography>
            </Box>
          </Card>
        </Box>

        {/* Products routed here Section */}
        <Box mb={4.5}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', mb: 0.5 }}>
            Products routed here
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.9rem', mb: 2 }}>
            Products that use this provider in their failover order.
          </Typography>

          <Card
            sx={{
              borderRadius: '18px',
              bgcolor: '#FFFFFF',
              boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
              border: '1px solid #ECEEF1',
              overflow: 'hidden',
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        borderBottom: '1px solid #F0F2F5',
                        color: '#6B7280',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        py: 2,
                        bgcolor: 'transparent',
                      },
                    }}
                  >
                    <TableCell sx={{ pl: 3 }}>ID</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell align="right" sx={{ pr: 3 }}>{/* Actions */}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignedProducts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        <Typography variant="body2" color="text.secondary">
                          No products are currently configured to route through this provider account.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    assignedProducts.map((prod) => {
                      const routeItem = prod.processorRoutes?.find((r) => r.processorSlug === provider.slug);
                      const priorityNum = routeItem?.priority || 1;
                      const catName = prod.service || prod.category || 'Airtime';
                      const capitalizedCat = catName.charAt(0).toUpperCase() + catName.slice(1).toLowerCase();

                      return (
                        <TableRow
                          key={prod.id}
                          hover
                          sx={{
                            '& td': {
                              borderBottom: '1px solid #F3F4F6',
                              py: 2,
                            },
                          }}
                        >
                          <TableCell sx={{ pl: 3 }}>
                            <Typography sx={{ color: '#111827', fontWeight: 500, fontSize: '0.9rem' }}>
                              #{prod.id}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography sx={{ color: '#111827', fontWeight: 600, fontSize: '0.925rem' }}>
                              {prod.details?.name || `${capitalizedCat} Plan`}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.35,
                                borderRadius: '12px',
                                bgcolor: '#F3F4F6',
                                color: '#374151',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                              }}
                            >
                              {capitalizedCat}
                            </Box>
                          </TableCell>

                          <TableCell>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                px: 1.5,
                                py: 0.35,
                                borderRadius: '12px',
                                bgcolor: '#EFF6FF',
                                color: '#2563EB',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                              }}
                            >
                              Priority {priorityNum}
                            </Box>
                          </TableCell>

                          <TableCell align="right" sx={{ pr: 3 }}>
                            <Typography
                              component="button"
                              onClick={() => navigate(`/dashboard/services/details/${prod.id}`)}
                              sx={{
                                background: 'none',
                                border: 'none',
                                p: 0,
                                color: '#0066FE',
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              View
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>

        {/* Top Up / Adjust Balance Modal */}
        <Dialog
          open={topUpModalOpen}
          onClose={() => !adjusting && setTopUpModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: { borderRadius: '20px', p: 1 },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827' }}>
              Adjust Wallet Balance
            </Typography>
            <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
              Update or top up balance for <strong>{provider.name}</strong> ({provider.slug}).
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ pt: 2 }}>
            {/* Current Balance Card */}
            <Box
              sx={{
                p: 2.5,
                mb: 3,
                borderRadius: '14px',
                bgcolor: '#F9FAFB',
                border: '1px solid #E5E7EB',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <div>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600 }}>
                    CURRENT RECORDED BALANCE
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827' }}>
                    ₦{balanceNum.toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </Typography>
                </div>
                <Chip
                  label={getProviderModeInfo(provider).shortLabel}
                  size="small"
                  sx={{ fontWeight: 600, bgcolor: '#EFF6FF', color: '#1D4ED8' }}
                />
              </Stack>
            </Box>

            {/* Action Selection */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#374151' }}>
              Adjustment Action
            </Typography>
            <RadioGroup
              value={adjustAction}
              onChange={(e) => setAdjustAction(e.target.value)}
              sx={{ mb: 2.5 }}
            >
              <FormControlLabel
                value="add"
                control={<Radio sx={{ color: '#0066FE', '&.Mui-checked': { color: '#0066FE' } }} />}
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      Add to Balance (Top-up)
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      e.g. "I've added ₦40,000" (Increments the current balance)
                    </Typography>
                  </Box>
                }
                sx={{ mb: 1, width: '100%' }}
              />
              <FormControlLabel
                value="set"
                control={<Radio sx={{ color: '#0066FE', '&.Mui-checked': { color: '#0066FE' } }} />}
                label={
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      Set Exact Balance
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      Directly overrides the current balance
                    </Typography>
                  </Box>
                }
                sx={{ width: '100%' }}
              />
            </RadioGroup>

            {/* Amount Field */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#374151' }}>
              Amount (₦)
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="e.g. 40000"
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 700 }}>₦</Typography>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />

            {/* Quick Preset Buttons */}
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
              {[10000, 20000, 40000, 50000, 100000].map((preset) => (
                <Chip
                  key={preset}
                  label={`+₦${preset.toLocaleString()}`}
                  onClick={() => {
                    if (adjustAction === 'add') {
                      setAdjustAmount(preset);
                    } else {
                      setAdjustAmount(balanceNum + preset);
                    }
                  }}
                  clickable
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    border: '1px solid #E5E7EB',
                    bgcolor: '#FFFFFF',
                    '&:hover': { bgcolor: '#F3F4F6' },
                  }}
                />
              ))}
            </Stack>

            {/* Calculated Preview */}
            {adjustAmount && !Number.isNaN(Number(adjustAmount)) && Number(adjustAmount) > 0 && (
              <Box
                sx={{
                  p: 2,
                  mb: 2.5,
                  borderRadius: '12px',
                  bgcolor: '#ECFDF5',
                  border: '1px solid #A7F3D0',
                }}
              >
                <Typography variant="body2" sx={{ color: '#065F46', fontWeight: 600 }}>
                  New Balance will be:{' '}
                  <strong style={{ fontSize: '1.05rem' }}>
                    ₦
                    {(adjustAction === 'add'
                      ? balanceNum + Number(adjustAmount)
                      : Number(adjustAmount)
                    ).toLocaleString('en-NG', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </strong>
                </Typography>
              </Box>
            )}

            {/* Optional Audit Notes */}
            <TextField
              fullWidth
              label="Audit Notes (Optional)"
              placeholder="e.g. Added ₦40,000 via Moniepoint transfer"
              value={adjustNotes}
              onChange={(e) => setAdjustNotes(e.target.value)}
              multiline
              rows={2}
            />
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setTopUpModalOpen(false)}
              disabled={adjusting}
              sx={{ color: '#6B7280', textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleConfirmBalanceAdjustment}
              disabled={
                adjusting ||
                !adjustAmount ||
                Number.isNaN(Number(adjustAmount)) ||
                Number(adjustAmount) <= 0
              }
              startIcon={adjusting ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                borderRadius: '12px',
                bgcolor: '#0066FE',
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: '#0052CC' },
              }}
            >
              {(() => {
                if (adjusting) return 'Updating...';
                if (adjustAction === 'add') return 'Confirm Top-Up';
                return 'Update Balance';
              })()}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
